import { memo, useEffect, useState } from "react";
import { Receipt, X, Plus, Camera, Loader2, FileUp, ChevronDown, Pencil, Search, Download, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { EXTRA_CATS, CAT_LABEL, fmtDay, fmtShortDate, isToll, extraGroup, dateGroupHint } from "./budgetUtils";
import { ExtraEditForm } from "./ExtraEditForm";
import { TricountPush } from "./TricountPush";

export const ExtraCostsCard = memo(({ tripId, perDay, extras, members, expenses, onRefresh, tricountUrl, tricountNameMap, tricountAutoSync, tricountPushed }) => {
  const [openState, setOpenState] = useState(null);
  const [form, setForm] = useState({ label: "", amount: "", day: "", date: "", category: "overig", kwh: "", inBudget: true });
  const [split, setSplit] = useState({ share: false, paidBy: "", parts: [] });
  const [editingId, setEditingId] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importItems, setImportItems] = useState(null);
  const [importSplit, setImportSplit] = useState({ share: false, paidBy: "", parts: [] });
  const [showAllItems, setShowAllItems] = useState(false);
  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [listOpen, setListOpen] = useState(false);
  const hasDates = Boolean(perDay?.some((d) => d.date));
  const extrasTotal = extras.reduce((a, e) => a + (e.amount_eur || 0), 0);
  const sortedExtras = [...extras].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const q = query.trim().toLowerCase();
  const catCounts = extras.reduce((m, e) => { const c = e.category || "overig"; m[c] = (m[c] || 0) + 1; return m; }, {});
  const catExtras = catFilter ? sortedExtras.filter((e) => (e.category || "overig") === catFilter) : sortedExtras;
  const filteredExtras = q ? catExtras.filter((e) =>
    (e.label || "").toLowerCase().includes(q) ||
    (CAT_LABEL[e.category] || e.category || "").toLowerCase().includes(q) ||
    (e.day_number != null && `dag ${e.day_number}`.includes(q)) ||
    (["vooraf", "achteraf"].includes(extraGroup(e, perDay)) && extraGroup(e, perDay).includes(q)) ||
    (e.date || "").includes(q) ||
    String(e.amount_eur ?? "").includes(q)
  ) : catExtras;
  const filtering = Boolean(q || catFilter);
  const visibleExtras = filtering || showAllItems || filteredExtras.length <= 20 ? filteredExtras : filteredExtras.slice(0, 15);

  useEffect(() => {
    setSplit((s) => ({ ...s, paidBy: s.paidBy || members[0] || "", parts: s.parts.length ? s.parts : [...members] }));
    setImportSplit((s) => ({ ...s, paidBy: s.paidBy || members[0] || "", parts: s.parts.length ? s.parts : [...members] }));
  }, [members]);

  const notifySaved = () => {
    window.dispatchEvent(new Event("vr-split-refresh"));
    onRefresh?.();
  };

  const addExtra = async () => {
    if (!form.label.trim() || !form.amount) return;
    const wantSplit = split.share || !form.inBudget;
    if (!form.inBudget && !split.paidBy) {
      toast.error("Kies wie betaalde — een uitgave buiten het budget moet verdeeld worden");
      return;
    }
    if (wantSplit && split.paidBy && members.length > 0 && split.parts.length === 0) {
      toast.error("Kies minstens één reisgenoot om over te verdelen");
      return;
    }
    try {
      const { data: extraRes } = await api.post(`/trips/${tripId}/extra-costs`, {
        label: form.label.trim(),
        amount_eur: parseFloat(form.amount),
        day_number: hasDates ? null : (form.day === "" ? null : parseInt(form.day)),
        date: hasDates ? (form.date || null) : null,
        category: form.category || null,
        kwh: form.kwh === "" ? null : parseFloat(form.kwh),
        in_budget: form.inBudget,
        split_paid_by: wantSplit && split.paidBy ? split.paidBy : null,
        split_participants: wantSplit && split.paidBy ? split.parts : null,
      });
      toast.success(!form.inBudget ? "Uitgave toegevoegd aan de verdeling (telt niet mee in budget)"
        : wantSplit && split.paidBy ? "Uitgave toegevoegd én verdeeld met reisgenoten" : "Uitgave toegevoegd");
      if (extraRes?.budget_alert) toast.warning(`Let op: je zit nu € ${extraRes.budget_alert.delta.toFixed(0)} (+${extraRes.budget_alert.pct}%) boven je raming.`, { duration: 8000 });
      setForm({ label: "", amount: "", day: "", date: "", category: "overig", kwh: "", inBudget: true });
      setSplit((s) => ({ ...s, parts: [...members] }));
      notifySaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteExtra = async (id) => {
    try {
      await api.delete(`/trips/${tripId}/extra-costs/${id}`);
      toast.success("Onkost verwijderd");
      notifySaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/trips/${tripId}/extra-costs/export.csv`, { responseType: "blob" });
      const fname = (res.headers["content-disposition"] || "").match(/filename="(.+?)"/)?.[1] || "onkosten.csv";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = fname;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("CSV gedownload — opent netjes in Excel of Numbers");
    } catch {
      toast.error("Export mislukt — probeer opnieuw");
    }
  };

  const scanReceipt = async (file) => {
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/trips/${tripId}/expense-scan`, fd, { timeout: 90000 });
      setForm((f) => ({
        ...f,
        label: data.label || f.label,
        amount: data.amount_eur != null ? String(data.amount_eur) : f.amount,
        day: data.day_number != null ? String(data.day_number) : f.day,
        date: data.date || f.date,
        category: data.category || f.category,
        kwh: data.kwh != null ? String(data.kwh) : "",
      }));
      toast.success(`Bon gelezen: ${data.label} — € ${data.amount_eur.toFixed(2)}${data.kwh ? ` (${data.kwh} kWh)` : ""}. Controleer en klik Toevoegen.`, { duration: 7000 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setScanning(false);
    }
  };

  const importFile = async (file) => {
    if (!file) return;
    setImportBusy(true);
    setImportItems(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/trips/${tripId}/expenses-import`, fd, { timeout: 180000 });
      setImportItems((data.items || []).map((it, i) => ({ ...it, _key: i, checked: !it.already_present, paidBy: "", day: it.day_number != null ? String(it.day_number) : "" })));
      if (data.already_present_count > 0) {
        toast.info(`${data.already_present_count} uitgave${data.already_present_count === 1 ? " staat" : "n staan"} al in de app (zelfde bedrag + datum) — automatisch uitgevinkt`, { duration: 7000 });
      }
      toast.success(`${(data.items || []).length} uitgaven herkend — vink aan wat je wilt toevoegen`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setImportBusy(false);
    }
  };

  const confirmImport = async () => {
    const sel = (importItems || []).filter((i) => i.checked);
    if (!sel.length) return;
    if (importSplit.share && !importSplit.paidBy) {
      toast.error("Kies wie betaalde, of zet de verdeling uit");
      return;
    }
    if (importSplit.share && importSplit.parts.length === 0) {
      toast.error("Kies minstens één reisgenoot om over te verdelen");
      return;
    }
    setImportBusy(true);
    let ok = 0;
    let splitCount = 0;
    for (const it of sel) {
      const payer = importSplit.share ? (it.paidBy || importSplit.paidBy) : null;
      try {
        await api.post(`/trips/${tripId}/extra-costs`, {
          label: it.label,
          amount_eur: it.amount_eur,
          day_number: it.day === "" ? null : parseInt(it.day),
          date: it.date || null,
          category: it.category || null,
          kwh: it.kwh || null,
          in_budget: true,
          split_paid_by: payer,
          split_participants: payer ? importSplit.parts : null,
        });
        ok += 1;
        if (payer) splitCount += 1;
      } catch { /* item overslaan */ }
    }
    setImportBusy(false);
    setImportItems(null);
    toast.success(`${ok} onkosten toegevoegd${splitCount ? ` én ${splitCount} verdeeld met reisgenoten` : ""}`);
    notifySaved();
  };

  const open = (openState ?? extras.length <= 8) || importItems != null || editingId != null;

  return (
    <div className="bg-card rounded-2xl p-5" data-testid="extra-costs-section">
      <button type="button" data-testid="extra-costs-toggle" onClick={() => setOpenState(!open)}
        className="w-full flex items-center justify-between gap-2 mb-1">
        <span className="font-heading font-bold text-sm flex items-center gap-1.5">
          <Receipt className="w-4 h-4 text-[#B5838D]" /> Onkosten &amp; uitgaven
          {extras.length > 0 && <span className="text-[11px] font-semibold text-muted-foreground">({extras.length})</span>}
        </span>
        <span className="flex items-center gap-2">
          {extras.length > 0 && <span data-testid="extra-costs-total" className="font-mono font-bold text-sm">€ {extrasTotal.toFixed(2)}</span>}
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {!open && (
        <p className="text-[11px] text-muted-foreground" data-testid="extra-costs-collapsed-hint">Klik om uit te klappen — hier voeg je uitgaven toe, scan je bonnetjes en beheer je de verdeling.</p>
      )}
      {open && (<>
      <p className="text-[11px] text-muted-foreground mb-3">Al je werkelijke uitgaven op één plek: verblijf, laden/vervoer, eten, tol, parkeren, tickets... Kies categorie en datum — ook kosten van vóór of na de reis (boekingen, ferry, vignet) horen erbij — en verdeel meteen met je reisgenoten.</p>
      <div className="grid grid-cols-2 gap-2 items-center">
        <input data-testid="extra-cost-label-input" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
          placeholder="Omschrijving (bv. tol Frankrijk)" maxLength={80}
          className="col-span-2 bg-secondary rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200" />
        <input data-testid="extra-cost-amount-input" type="number" min="0" step="0.01" value={form.amount}
          onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="€"
          className="bg-secondary rounded-xl px-3.5 py-2.5 text-sm font-mono outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200" />
        <select data-testid="extra-cost-category-select" value={form.category}
          onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
          className="bg-secondary rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200">
          {EXTRA_CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {hasDates ? (
          <input data-testid="extra-cost-date-input" type="date" value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            title="Datum van de uitgave — mag ook vóór of na de reis liggen"
            className="bg-secondary rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200" />
        ) : (
        <select data-testid="extra-cost-day-select" value={form.day} onChange={(e) => setForm((f) => ({ ...f, day: e.target.value }))}
          className="bg-secondary rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200">
          <option value="">Hele trip</option>
          {perDay?.map((d) => <option key={d.day_number} value={d.day_number}>{`Dag ${d.day_number}${d.date ? ` · ${fmtDay(d.date)}` : ""}`}</option>)}
        </select>
        )}
        <button data-testid="extra-cost-add-button" onClick={addExtra} disabled={!form.label.trim() || !form.amount || (split.share && !split.paidBy)}
          className="rounded-full bg-primary hover:bg-[#1B4332] disabled:opacity-40 text-primary-foreground text-xs font-bold py-2.5 px-4 flex items-center justify-center gap-1 active:scale-95 transition-[transform,background-color] duration-200">
          <Plus className="w-3.5 h-3.5" /> Toevoegen
        </button>
      </div>
      {hasDates && (
        <p className="text-[10px] text-muted-foreground mt-1" data-testid="extra-date-hint">{dateGroupHint(form.date, perDay)}</p>
      )}
      {form.kwh !== "" && (
        <p className="text-[10px] text-muted-foreground mt-1 font-mono" data-testid="scanned-kwh">geladen: {form.kwh} kWh (wordt mee opgeslagen)</p>
      )}
      <div className="mt-3 bg-secondary/40 rounded-xl p-3" data-testid="extra-split-box">
        <p className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-2">Voor deze nieuwe uitgave</p>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <input data-testid="extra-cost-inbudget-check" type="checkbox" checked={form.inBudget}
            onChange={(e) => {
              const on = e.target.checked;
              setForm((f) => ({ ...f, inBudget: on }));
              if (!on) setSplit((s) => ({ ...s, share: true, parts: s.parts.length ? s.parts : [...members] }));
            }} className="accent-[#2D6A4F] w-3.5 h-3.5" />
          Telt mee in tripbudget
          {!form.inBudget && <span className="italic text-[10px]" data-testid="extra-nobudget-hint">— wordt alleen onderling verdeeld</span>}
        </label>
        <label className="flex flex-wrap items-center gap-2 mt-2 text-xs text-muted-foreground cursor-pointer">
          <input data-testid="extra-cost-split-check" type="checkbox" checked={split.share} disabled={!form.inBudget}
            onChange={(e) => setSplit((s) => ({ ...s, share: e.target.checked, parts: s.parts.length ? s.parts : [...members] }))} className="accent-[#3A6EA5] w-3.5 h-3.5" />
          Ook verdelen met reisgenoten
          {split.share && (members.length > 0 ? (
            <>
              <span>— betaald door</span>
              <select data-testid="extra-cost-split-paidby" value={split.paidBy}
                onChange={(e) => setSplit((s) => ({ ...s, paidBy: e.target.value }))}
                className="bg-secondary rounded-lg px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200">
                {members.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </>
          ) : (
            <span className="italic" data-testid="extra-split-no-members">voeg eerst reisgenoten toe bij "Kosten verdelen" hieronder</span>
          ))}
        </label>
        {split.share && members.length > 1 && (
          <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs text-muted-foreground" data-testid="extra-cost-split-parts">
            <span>verdeeld over:</span>
            {members.map((m) => {
              const on = split.parts.includes(m);
              return (
                <button key={m} type="button" data-testid={`extra-part-${m}`}
                  onClick={() => setSplit((s) => ({ ...s, parts: on ? s.parts.filter((x) => x !== m) : [...s.parts, m] }))}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-[background-color,color,border-color] duration-150 ${on ? "bg-[#3A6EA5] text-white border-[#3A6EA5]" : "bg-card border-border text-muted-foreground hover:border-[#3A6EA5]/50"}`}>
                  {m}
                </button>
              );
            })}
          </div>
        )}
        <p className="text-[10px] text-muted-foreground mt-2">Tip: bestaande onkosten pas je aan (ook de verdeling) via het potloodje bij het item of bij de dag.</p>
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3">
        <label className={`flex items-center gap-1.5 text-xs font-semibold cursor-pointer transition-[color] duration-200 ${scanning ? "text-muted-foreground" : "text-[#3A6EA5] hover:text-[#2a5580]"}`}>
          <input data-testid="expense-scan-input" type="file" accept="image/*" className="hidden" disabled={scanning}
            onChange={(e) => { scanReceipt(e.target.files?.[0]); e.target.value = ""; }} />
          {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
          {scanning ? "Bon wordt gelezen..." : "Scan bonnetje of laderscherm"}
        </label>
        <label className={`flex items-center gap-1.5 text-xs font-semibold cursor-pointer transition-[color] duration-200 ${importBusy ? "text-muted-foreground" : "text-[#3A6EA5] hover:text-[#2a5580]"}`}>
          <input data-testid="expenses-import-input" type="file" accept="image/*,.pdf,.csv,.txt" className="hidden" disabled={importBusy}
            onChange={(e) => { importFile(e.target.files?.[0]); e.target.value = ""; }} />
          {importBusy && !importItems ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}
          {importBusy && !importItems ? "Afschrift wordt gelezen..." : "Importeer afschrift (VISA, Tesla, bank...)"}
        </label>
        {extras.length > 0 && (
          <button type="button" data-testid="expenses-export-csv" onClick={exportCsv}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#3A6EA5] hover:text-[#2a5580] transition-[color] duration-200">
            <Download className="w-3.5 h-3.5" /> Exporteer CSV
          </button>
        )}
      </div>
      <TricountPush tripId={tripId} prefillUrl={tricountUrl} members={members || []} prefillMap={tricountNameMap} onRefresh={onRefresh} autoSync={tricountAutoSync} />
      {importItems && (
        <div className="mt-3 bg-secondary/50 rounded-xl p-3" data-testid="import-review">
          <p className="text-xs font-bold mb-1.5">Herkende uitgaven — {importItems.filter((i) => i.checked).length}/{importItems.length} geselecteerd</p>
          <div className="bg-card/70 rounded-lg p-2.5 mb-2" data-testid="import-split-bar">
            <label className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground cursor-pointer">
              <input type="checkbox" data-testid="import-split-check" checked={importSplit.share}
                onChange={(e) => setImportSplit((s) => ({ ...s, share: e.target.checked, parts: s.parts.length ? s.parts : [...members] }))}
                className="accent-[#3A6EA5] w-3.5 h-3.5" />
              <span className="font-semibold">Verdelen met reisgenoten</span>
              {importSplit.share && (members.length > 0 ? (
                <>
                  <span>— betaald door</span>
                  <select data-testid="import-split-paidby" value={importSplit.paidBy}
                    onChange={(e) => setImportSplit((s) => ({ ...s, paidBy: e.target.value }))}
                    className="bg-secondary rounded-lg px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200">
                    {members.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <span className="text-[10px] italic">per uitgave aanpasbaar in de lijst hieronder</span>
                </>
              ) : (
                <span className="italic" data-testid="import-split-no-members">voeg eerst reisgenoten toe bij "Kosten verdelen" hieronder</span>
              ))}
            </label>
            {importSplit.share && members.length > 1 && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs text-muted-foreground" data-testid="import-split-parts">
                <span>verdeeld over:</span>
                {members.map((m) => {
                  const on = importSplit.parts.includes(m);
                  return (
                    <button key={m} type="button" data-testid={`import-part-${m}`}
                      onClick={() => setImportSplit((s) => ({ ...s, parts: on ? s.parts.filter((x) => x !== m) : [...s.parts, m] }))}
                      className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-[background-color,color,border-color] duration-150 ${on ? "bg-[#3A6EA5] text-white border-[#3A6EA5]" : "bg-card border-border text-muted-foreground hover:border-[#3A6EA5]/50"}`}>
                      {m}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="space-y-0.5 max-h-72 overflow-y-auto pr-1">
            {importItems.map((it) => (
              <div key={it._key} data-testid={`import-item-${it._key}`} className={`flex flex-wrap items-center gap-1.5 text-xs border-b border-border/40 last:border-0 py-1.5 ${it.checked ? "" : "opacity-50"}`}>
                <input type="checkbox" data-testid={`import-check-${it._key}`} checked={it.checked}
                  onChange={() => setImportItems((arr) => arr.map((x) => x._key === it._key ? { ...x, checked: !x.checked } : x))}
                  className="accent-[#2D6A4F] w-3.5 h-3.5 cursor-pointer" />
                <span className="flex-1 min-w-[110px] truncate font-semibold">{it.label}{it.date ? <span className="text-muted-foreground font-normal"> · {it.date}</span> : null}</span>
                {it.already_present && (
                  <span data-testid={`import-dup-${it._key}`} title="Er staat al een uitgave met exact dit bedrag en deze datum in de app"
                    className="text-[10px] font-bold uppercase tracking-wide text-[#946200] bg-amber-100 rounded-full px-2 py-0.5 shrink-0">staat al in de app</span>
                )}
                <span className="font-mono font-bold shrink-0">€ {it.amount_eur.toFixed(2)}</span>
                <select value={it.category} data-testid={`import-cat-${it._key}`}
                  onChange={(e) => setImportItems((arr) => arr.map((x) => x._key === it._key ? { ...x, category: e.target.value } : x))}
                  className="bg-card rounded-lg px-1.5 py-1 outline-none focus:ring-2 focus:ring-primary">
                  {EXTRA_CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <select value={it.day} data-testid={`import-day-${it._key}`}
                  onChange={(e) => setImportItems((arr) => arr.map((x) => x._key === it._key ? { ...x, day: e.target.value } : x))}
                  className="bg-card rounded-lg px-1.5 py-1 outline-none focus:ring-2 focus:ring-primary">
                  <option value="">Hele trip</option>
                  {perDay?.map((d) => <option key={d.day_number} value={d.day_number}>{`Dag ${d.day_number}`}</option>)}
                </select>
                {importSplit.share && members.length > 0 && (
                  <select value={it.paidBy || ""} data-testid={`import-paidby-${it._key}`}
                    title="Wie betaalde deze uitgave?"
                    onChange={(e) => setImportItems((arr) => arr.map((x) => x._key === it._key ? { ...x, paidBy: e.target.value } : x))}
                    className={`bg-card rounded-lg px-1.5 py-1 outline-none focus:ring-2 focus:ring-primary ${it.paidBy ? "text-[#3A6EA5] font-semibold" : ""}`}>
                    <option value="">betaald: {importSplit.paidBy || "—"}</option>
                    {members.filter((m) => m !== importSplit.paidBy).map((m) => <option key={m} value={m}>betaald: {m}</option>)}
                  </select>
                )}
              </div>
            ))}
          </div>
          {importSplit.share && importSplit.paidBy && <p className="text-[10px] text-muted-foreground mt-1.5" data-testid="import-split-hint">Alle aangevinkte uitgaven worden verdeeld — standaard betaald door {importSplit.paidBy}, tenzij je het per uitgave aanpast.</p>}
          <div className="flex gap-2 mt-2">
            <button data-testid="import-confirm" onClick={confirmImport} disabled={importBusy || !importItems.some((i) => i.checked)}
              className="rounded-full bg-primary hover:bg-[#1B4332] disabled:opacity-40 text-primary-foreground text-xs font-bold py-2 px-4 flex items-center gap-1 active:scale-95 transition-[transform,background-color] duration-200">
              {importBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Voeg {importItems.filter((i) => i.checked).length} toe
            </button>
            <button data-testid="import-cancel" onClick={() => setImportItems(null)}
              className="rounded-full bg-secondary hover:bg-secondary/70 text-xs font-semibold py-2 px-4 transition-[background-color] duration-200">Annuleer</button>
          </div>
        </div>
      )}
      {extras.length > 0 && !listOpen && (
        <button type="button" data-testid="extra-costs-list-toggle" onClick={() => setListOpen(true)}
          className="w-full mt-3 bg-secondary/50 hover:bg-secondary rounded-xl py-2.5 px-3 text-xs font-semibold text-[#3A6EA5] flex items-center justify-center gap-1.5 transition-[background-color] duration-200">
          <ChevronDown className="w-3.5 h-3.5" /> Toon alle {extras.length} uitgaven (€ {extrasTotal.toFixed(2)}) — zoeken, filteren & bewerken
        </button>
      )}
      {extras.length > 0 && listOpen && (
        <div className="space-y-0.5 mt-3">
          <button type="button" data-testid="extra-costs-list-hide" onClick={() => setListOpen(false)}
            className="w-full mb-2 bg-secondary/50 hover:bg-secondary rounded-xl py-2 px-3 text-xs font-semibold text-muted-foreground flex items-center justify-center gap-1.5 transition-[background-color] duration-200">
            <ChevronDown className="w-3.5 h-3.5 rotate-180" /> Verberg lijst — je uitgaven blijven ook zichtbaar bij "Per dag"
          </button>
          {extras.length > 5 && (
            <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="extra-costs-cat-filters">
              <button type="button" data-testid="extra-cat-filter-alle" onClick={() => setCatFilter("")}
                className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-[background-color,color,border-color] duration-150 ${!catFilter ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:border-primary/50"}`}>
                Alle ({extras.length})
              </button>
              {EXTRA_CATS.filter(([v]) => catCounts[v]).map(([v, l]) => (
                <button key={v} type="button" data-testid={`extra-cat-filter-${v}`}
                  onClick={() => setCatFilter((c) => c === v ? "" : v)}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-[background-color,color,border-color] duration-150 ${catFilter === v ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:border-primary/50"}`}>
                  {l} ({catCounts[v]})
                </button>
              ))}
            </div>
          )}
          {extras.length > 5 && (
            <div className="relative mb-2" data-testid="extra-costs-search-box">
              <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input data-testid="extra-costs-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder={`Zoek in ${extras.length} uitgaven (naam, categorie, dag, bedrag...)`}
                className="w-full bg-secondary rounded-xl pl-9 pr-8 py-2 text-xs outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200" />
              {query && (
                <button data-testid="extra-costs-search-clear" onClick={() => setQuery("")} title="Wis zoekopdracht"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-card text-muted-foreground transition-[background-color] duration-200">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
          {filtering && (
            <p className="text-[11px] text-muted-foreground pb-1" data-testid="extra-costs-search-count">
              {filteredExtras.length === 0 ? "Geen uitgaven gevonden" : `${filteredExtras.length} van ${extras.length} uitgaven`}
              {filteredExtras.length > 0 && <span className="font-mono font-semibold"> · € {filteredExtras.reduce((a, e) => a + (e.amount_eur || 0), 0).toFixed(2)}</span>}
            </p>
          )}
          {visibleExtras.map((e) => editingId === e.id ? (
            <ExtraEditForm key={e.id} tripId={tripId} item={e} splitExpense={(expenses || []).find((x) => x.id === e.split_expense_id)}
              members={members} perDay={perDay} onCancel={() => setEditingId(null)}
              onSaved={() => { setEditingId(null); notifySaved(); }} />
          ) : (
            <div key={e.id} data-testid={`extra-cost-item-${e.id}`} className="flex items-center justify-between gap-2 text-sm border-b border-border/60 last:border-0 py-1.5">
              <span className="min-w-0 truncate">
                {e.label}
                {e.category && <span data-testid={`extra-cat-badge-${e.id}`} className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground bg-secondary rounded-full px-2 py-0.5">{CAT_LABEL[e.category] || e.category}</span>}
                {e.day_number != null && <span className="text-xs text-muted-foreground"> · dag {e.day_number}{(() => { const dd = e.date || perDay?.find((d) => d.day_number === e.day_number)?.date; return dd ? ` (${fmtDay(dd)})` : ""; })()}</span>}
                {(() => { const g = extraGroup(e, perDay); return (g === "vooraf" || g === "achteraf") ? (
                  <span data-testid={`extra-outside-badge-${e.id}`} className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-[#946200] bg-amber-100 rounded-full px-2 py-0.5">{g}{e.date ? ` · ${fmtShortDate(e.date)}` : ""}</span>
                ) : null; })()}
                {e.split_expense_id && <span data-testid={`extra-split-badge-${e.id}`} className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-[#3A6EA5] bg-[#3A6EA5]/10 rounded-full px-2 py-0.5">verdeeld</span>}
                {isToll(e) && <span data-testid={`extra-toll-badge-${e.id}`} className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-[#B5838D] bg-[#B5838D]/10 rounded-full px-2 py-0.5">tol</span>}
              </span>
              <span className="flex items-center gap-1.5 shrink-0">
                {e.split_expense_id && tricountUrl && tricountPushed?.[e.split_expense_id] && (
                  <span data-testid={`extra-tricount-synced-${e.id}`} title="Gesynct met Tricount" className="flex items-center">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#2D6A4F]" />
                  </span>
                )}
                <span className="font-mono font-semibold">€ {e.amount_eur.toFixed(2)}</span>
                <button data-testid={`extra-cost-edit-${e.id}`} onClick={() => setEditingId(e.id)} title="Onkost aanpassen"
                  className="p-1 rounded-full hover:bg-secondary text-muted-foreground transition-[background-color] duration-200">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button data-testid={`extra-cost-delete-${e.id}`} onClick={() => deleteExtra(e.id)}
                  className="p-1 rounded-full hover:bg-red-50 text-red-500 transition-[background-color] duration-200">
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            </div>
          ))}
          {!filtering && !showAllItems && extras.length > 20 && (
            <button type="button" data-testid="extra-costs-show-all" onClick={() => setShowAllItems(true)}
              className="w-full text-xs font-semibold text-[#3A6EA5] hover:text-[#2a5580] py-2 transition-[color] duration-200">
              Toon alle {extras.length} onkosten ({extras.length - visibleExtras.length} verborgen)
            </button>
          )}
        </div>
      )}
      </>)}
    </div>
  );
});
